import styled from 'styled-components';
import { getColor } from 'utils';

const isDarkTheme = props => (props.$selectedTheme || props.selectedTheme) === 'dark';

// Secondary/muted text. In dark mode the `darkGrey` token (#383838) is nearly invisible on
// the dark surface, so use a readable light-grey; light mode keeps the original token.
const mutedText = props => (isDarkTheme(props) ? '#9aa7b0' : getColor(props, 'darkGrey'));

// Subtle filled surface (helper box, disabled button). Dark `lightGrey` (#767676) is too
// bright to read muted text on, so drop to mediumGrey in dark; light mode is unchanged.
const subtleSurface = props => (isDarkTheme(props) ? getColor(props, 'mediumGrey') : getColor(props, 'lightGrey'));

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 4.5rem);
  background-color: ${props => getColor(props, 'primary')};
  overflow: hidden;
`;

export const TopBar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 3.25rem;
  padding: 0.375rem 1rem;
  border-bottom: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};
  background-color: ${props => getColor(props, 'primary')};
  flex-shrink: 0;
  gap: 1rem;
  flex-wrap: wrap;
`;

export const TopBarSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.$gap || '0.5rem'};
  flex: ${props => props.$flex || 'none'};
  justify-content: ${props => props.$justify || 'flex-start'};
`;

export const ImageNameButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.5rem;
  border: none;
  border-radius: 0.375rem;
  background: transparent;
  color: ${props => getColor(props, 'onPrimary')};
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;

  &:hover {
    background: ${props => getColor(props, 'lightGrey')};
  }
`;

export const IconTextButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 0.375rem;
  background: transparent;
  color: ${props => getColor(props, 'onPrimary')};
  cursor: pointer;
  font-size: 1rem;

  &:hover:not(:disabled) {
    background: ${props => getColor(props, 'lightGrey')};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

export const ZoomControlGroup = styled.div`
  display: flex;
  align-items: center;
  border: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};
  border-radius: 0.5rem;
  overflow: hidden;
`;

export const ZoomButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  background: transparent;
  color: ${props => getColor(props, 'onPrimary')};
  cursor: pointer;
  font-size: 1.125rem;
  line-height: 1;

  &:hover {
    background: ${props => getColor(props, 'lightGrey')};
  }
`;

export const ZoomValue = styled.span`
  font-size: 0.8125rem;
  color: ${props => getColor(props, 'onPrimary')};
  min-width: 3.25rem;
  text-align: center;
  user-select: none;
  border-left: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};
  border-right: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};
  padding: 0 0.25rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ContentArea = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  min-height: 0;
  position: relative;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const Generate3DOverlayRoot = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 22, 36, 0.55);
  backdrop-filter: blur(0.15rem);
  z-index: 20;
  padding: 1.5rem;
`;

export const Generate3DOverlayCard = styled.div`
  width: 100%;
  max-width: 26rem;
  background: ${props => getColor(props, 'primary')};
  border: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};
  border-radius: 0.875rem;
  padding: 1.5rem 1.5rem 1.25rem;
  box-shadow: 0 1.25rem 3rem rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
`;

export const Generate3DOverlayTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${props => getColor(props, 'onPrimary')};
`;

export const Generate3DOverlaySpinner = styled.span`
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 0.1875rem solid ${props => getColor(props, 'mediumGrey')};
  border-top-color: ${props => getColor(props, 'accent')};
  border-radius: 50%;
  animation: generate3dSpin 0.8s linear infinite;

  @keyframes generate3dSpin {
    to { transform: rotate(360deg); }
  }
`;

export const Generate3DOverlayStatus = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: ${props => mutedText(props)};
  line-height: 1.4;
`;

export const Generate3DProgressTrack = styled.div`
  width: 100%;
  height: 0.5rem;
  background: ${props => getColor(props, 'lightGrey')};
  border-radius: 0.25rem;
  overflow: hidden;
`;

export const Generate3DProgressFill = styled.div`
  height: 100%;
  width: ${props => Math.max(0, Math.min(100, props.$progress || 0))}%;
  background: ${props => getColor(props, 'accent')};
  border-radius: 0.25rem;
  transition: width 0.3s ease;
`;

export const Generate3DProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: ${props => mutedText(props)};
  font-variant-numeric: tabular-nums;
`;

export const Generate3DErrorText = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: ${props => getColor(props, 'error')};
  line-height: 1.4;
`;

export const Generate3DActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.25rem;
`;

export const Generate3DActionButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  border: 0.0625rem solid ${props => props.$primary
    ? getColor(props, 'accent')
    : getColor(props, 'mediumGrey')};
  background: ${props => props.$primary
    ? getColor(props, 'accent')
    : getColor(props, 'primary')};
  color: ${props => props.$primary ? '#ffffff' : getColor(props, 'onPrimary')};
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover:not(:disabled) {
    background: ${props => props.$primary
      ? getColor(props, 'accentHover')
      : getColor(props, 'lightGrey')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const CanvasColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
`;

export const CanvasSection = styled.main`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => getColor(props, 'primary')};
  position: relative;
  overflow: hidden;
  min-width: 0;
`;

export const CanvasArea = styled.div`
  width: 88%;
  height: 88%;
  max-width: 58rem;
  max-height: 40rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

export const DropZone = styled.div`
  width: 100%;
  height: 100%;
  min-height: 18rem;
  border: 0.125rem dashed ${props => props.$isDragOver
    ? getColor(props, 'accent')
    : getColor(props, 'mediumGrey')};
  border-radius: 0.75rem;
  background: ${props => props.$isDragOver
    ? props.theme.hexToRgba(getColor(props, 'accent'), 0.12)
    : getColor(props, 'primary')};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
  user-select: none;
  padding: 2rem 1.5rem;
  box-shadow: ${props => props.$isDragOver ? '0 0 0 0.25rem rgba(53, 158, 255, 0.12)' : 'none'};

  &:hover {
    border-color: ${props => getColor(props, 'accent')};
  }
`;

export const UploadIconWrapper = styled.div`
  font-size: 3.25rem;
  color: ${props => getColor(props, 'accent')};
  margin-bottom: 1rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4.5rem;
  height: 4.5rem;
  border-radius: 50%;
  background: ${props => props.theme.hexToRgba(getColor(props, 'accent'), 0.15)};
`;

export const DropZonePrimary = styled.p`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${props => getColor(props, 'onPrimary')};
  margin: 0;
`;

export const DropZoneSecondary = styled.p`
  font-size: 0.875rem;
  color: ${props => mutedText(props)};
  margin: 0.375rem 0 0;
`;

export const DropZoneUploadButton = styled.button`
  margin-top: 1.25rem;
  padding: 0.625rem 1.5rem;
  background: ${props => getColor(props, 'accent')};
  color: #ffffff;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.9375rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: background 0.15s ease;

  &:hover {
    background: ${props => getColor(props, 'accentHover')};
  }
`;

export const DropZoneFormats = styled.p`
  font-size: 0.75rem;
  color: ${props => mutedText(props)};
  margin: 1rem 0 0;
  opacity: 0.85;
`;

export const HiddenFileInput = styled.input`
  display: none;
`;

export const ErrorText = styled.p`
  font-size: 0.875rem;
  color: ${props => getColor(props, 'error')};
  margin: 0.75rem 0 0;
  text-align: center;
`;

export const ImageFrame = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transform: scale(${props => props.$zoom / 100});
  transform-origin: center center;
  transition: transform 0.1s ease;
`;

export const UploadedImage = styled.img`
  display: block;
  max-width: 52rem;
  max-height: 36rem;
  object-fit: contain;
  user-select: none;
  pointer-events: none;
  transform: rotate(${props => props.$rotate || 0}deg);
  filter: ${props => props.$filter || 'none'};
  transition: transform 0.15s ease;
`;

export const SelectionBox = styled.div`
  position: absolute;
  inset: 0;
  border: 0.125rem solid #359eff;
  pointer-events: none;
`;

const Handle = styled.div`
  position: absolute;
  width: 0.5625rem;
  height: 0.5625rem;
  background: #ffffff;
  border: 0.125rem solid #359eff;
  border-radius: 50%;
  pointer-events: none;
`;

export const HandleTL = styled(Handle)`top: 0; left: 0; transform: translate(-50%, -50%);`;
export const HandleTC = styled(Handle)`top: 0; left: 50%; transform: translate(-50%, -50%);`;
export const HandleTR = styled(Handle)`top: 0; right: 0; transform: translate(50%, -50%);`;
export const HandleML = styled(Handle)`top: 50%; left: 0; transform: translate(-50%, -50%);`;
export const HandleMR = styled(Handle)`top: 50%; right: 0; transform: translate(50%, -50%);`;
export const HandleBL = styled(Handle)`bottom: 0; left: 0; transform: translate(-50%, 50%);`;
export const HandleBC = styled(Handle)`bottom: 0; left: 50%; transform: translate(-50%, 50%);`;
export const HandleBR = styled(Handle)`bottom: 0; right: 0; transform: translate(50%, 50%);`;

export const SidebarPanel = styled.aside`
  width: 19rem;
  flex-shrink: 0;
  background: ${props => getColor(props, 'primary')};
  border-left: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  @media (max-width: 768px) {
    width: 100%;
    border-left: none;
    border-top: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};
    max-height: 22rem;
  }
`;

export const SidebarContent = styled.div`
  padding: 1.25rem 1rem 1.5rem;
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
`;

export const SidebarTitle = styled.h2`
  font-size: 1rem;
  font-weight: 600;
  color: ${props => getColor(props, 'onPrimary')};
  margin: 0 0 1rem;
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-bottom: 0.625rem;
`;

export const SectionLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  color: ${props => mutedText(props)};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

export const BetaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.0625rem 0.375rem;
  background: ${props => props.theme.hexToRgba(getColor(props, 'accent'), 0.15)};
  color: ${props => getColor(props, 'accent')};
  border-radius: 0.25rem;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.02em;
`;

export const ToolCard = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.5625rem 0.75rem;
  border: 0.0625rem solid ${props => props.$active
    ? getColor(props, 'accent')
    : getColor(props, 'mediumGrey')};
  border-radius: 0.5rem;
  background: ${props => props.$active ? getColor(props, 'accent') : getColor(props, 'primary')};
  color: ${props => props.$active ? '#ffffff' : getColor(props, 'onPrimary')};
  cursor: pointer;
  text-align: left;
  margin-bottom: 0.5rem;
  transition: background 0.1s, border-color 0.1s, transform 0.1s;

  &:hover:not(:disabled) {
    border-color: ${props => getColor(props, 'accent')};
    background: ${props => props.$active ? getColor(props, 'accent') : getColor(props, 'lightGrey')};
  }

  &:active:not(:disabled) {
    transform: scale(0.99);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    background: ${props => getColor(props, 'primary')};
    color: ${props => mutedText(props)};
    border-color: ${props => getColor(props, 'mediumGrey')};
  }
`;

export const ToolCardLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`;

export const ToolCardIcon = styled.span`
  font-size: 0.9375rem;
  line-height: 1.35;
  flex-shrink: 0;
`;

export const ToolCardTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

export const ToolCardName = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.3;
`;

export const ToolCardDesc = styled.span`
  font-size: 0.6875rem;
  line-height: 1.3;
  opacity: 0.65;
`;

export const ChevronIcon = styled.span`
  font-size: 0.75rem;
  opacity: 0.4;
  flex-shrink: 0;
  margin-left: 0.25rem;
`;

export const ToolCardSpinner = styled.span`
  display: inline-block;
  width: 0.75rem;
  height: 0.75rem;
  border: 0.125rem solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  opacity: 0.75;
  flex-shrink: 0;
  margin-left: 0.25rem;
  animation: toolCardSpin 0.8s linear infinite;

  @keyframes toolCardSpin {
    to { transform: rotate(360deg); }
  }
`;

export const SidebarDivider = styled.div`
  height: 0.0625rem;
  background: ${props => getColor(props, 'mediumGrey')};
  margin: 0.75rem 0;
`;

export const ModelSelectWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 0.75rem;
`;

export const ModelPickerRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`;

export const ModelPickerSelect = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ModelPickerSettingsButton = styled.button`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};
  border-radius: 0.5rem;
  background: ${props => getColor(props, 'primary')};
  color: ${props => getColor(props, 'onPrimary')};
  font-size: 1.125rem;
  line-height: 1;
  cursor: pointer;
  outline: none;

  &:hover {
    border-color: ${props => getColor(props, 'accent')};
    background: ${props => getColor(props, 'lightGrey')};
  }

  &:focus {
    border-color: ${props => getColor(props, 'accent')};
  }
`;

export const ModelDropdown = styled.select`
  width: 100%;
  padding: 0.5rem 2rem 0.5rem 0.75rem;
  border: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};
  border-radius: 0.5rem;
  background: ${props => getColor(props, 'primary')};
  color: ${props => getColor(props, 'onPrimary')};
  font-size: 0.875rem;
  appearance: none;
  cursor: pointer;
  outline: none;
  height: 2.5rem;

  &:hover {
    border-color: ${props => getColor(props, 'accent')};
  }

  &:focus {
    border-color: ${props => getColor(props, 'accent')};
  }
`;

export const ModelDropdownArrow = styled.div`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: ${props => mutedText(props)};
  font-size: 0.625rem;
`;

export const GenerateButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem;
  background: ${props => props.disabled ? subtleSurface(props) : getColor(props, 'accent')};
  color: ${props => props.disabled ? mutedText(props) : '#ffffff'};
  border: 0.0625rem solid ${props => props.disabled ? getColor(props, 'mediumGrey') : getColor(props, 'accent')};
  border-radius: 0.5rem;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.15s, transform 0.1s;
  box-shadow: ${props => props.disabled ? 'none' : '0 0.125rem 0.5rem rgba(53, 158, 255, 0.25)'};

  &:hover:not(:disabled) {
    background: ${props => getColor(props, 'accentHover')};
  }

  &:active:not(:disabled) {
    transform: scale(0.99);
  }
`;

export const GenerateCaption = styled.p`
  font-size: 0.75rem;
  color: ${props => mutedText(props)};
  text-align: center;
  margin: 0.375rem 0 0;
`;

export const SidebarDownloadButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  background: ${props => getColor(props, 'primary')};
  color: ${props => getColor(props, 'onPrimary')};
  border: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};
  border-radius: 0.5rem;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover:not(:disabled) {
    border-color: ${props => getColor(props, 'accent')};
    background: ${props => getColor(props, 'lightGrey')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const CropOverlayRoot = styled.div`
  position: absolute;
  inset: 0;
  user-select: none;
  touch-action: none;
`;

export const CropDimRect = styled.div`
  position: absolute;
  background: rgba(0, 0, 0, 0.5);
  pointer-events: none;
  top: ${({ $top }) => $top ?? 'auto'};
  left: ${({ $left }) => $left ?? 'auto'};
  right: ${({ $right }) => $right ?? 'auto'};
  bottom: ${({ $bottom }) => $bottom ?? 'auto'};
  width: ${({ $w }) => $w ?? 'auto'};
  height: ${({ $h }) => $h ?? 'auto'};
`;

export const CropSelection = styled.div`
  position: absolute;
  left: ${({ $x }) => $x}%;
  top: ${({ $y }) => $y}%;
  width: ${({ $w }) => $w}%;
  height: ${({ $h }) => $h}%;
  border: 0.125rem solid rgba(255, 255, 255, 0.9);
  cursor: move;
  z-index: 1;
`;

export const CropHandle = styled.div`
  position: absolute;
  width: 0.625rem;
  height: 0.625rem;
  background: #ffffff;
  border: 0.125rem solid #359eff;
  border-radius: 50%;
  z-index: 2;
  ${({ $pos }) => {
    switch ($pos) {
      case 'tl': return 'top: 0; left: 0; transform: translate(-50%, -50%); cursor: nwse-resize;';
      case 'tc': return 'top: 0; left: 50%; transform: translate(-50%, -50%); cursor: ns-resize;';
      case 'tr': return 'top: 0; right: 0; transform: translate(50%, -50%); cursor: nesw-resize;';
      case 'ml': return 'top: 50%; left: 0; transform: translate(-50%, -50%); cursor: ew-resize;';
      case 'mr': return 'top: 50%; right: 0; transform: translate(50%, -50%); cursor: ew-resize;';
      case 'bl': return 'bottom: 0; left: 0; transform: translate(-50%, 50%); cursor: nesw-resize;';
      case 'bc': return 'bottom: 0; left: 50%; transform: translate(-50%, 50%); cursor: ns-resize;';
      case 'br': return 'bottom: 0; right: 0; transform: translate(50%, 50%); cursor: nwse-resize;';
      default: return '';
    }
  }}
`;

export const CropActionBar = styled.div`
  position: absolute;
  top: calc(100% + 0.75rem);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
  z-index: 10;
`;

export const CropActionButton = styled.button`
  padding: 0.375rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  background: ${({ $primary }) => $primary ? '#359eff' : 'rgba(30, 30, 30, 0.85)'};
  color: #ffffff;

  &:hover:not(:disabled) {
    background: ${({ $primary }) => $primary ? '#1e8ce0' : 'rgba(30, 30, 30, 1)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const RotateBar = styled.div`
  position: absolute;
  top: 1.25rem;
  right: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${props => getColor(props, 'primary')};
  border: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};
  border-radius: 1rem;
  padding: 0.4375rem 0.75rem;
  box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.18);
  z-index: 5;
  max-width: calc(100% - 2.5rem);
  flex-wrap: wrap;

  @media (max-width: 768px) {
    top: auto;
    right: 0.75rem;
    left: 0.75rem;
    bottom: 0.75rem;
    justify-content: center;
  }
`;

export const RotateIconButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  height: 2rem;
  padding: 0 0.625rem;
  border: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};
  border-radius: 1rem;
  background: ${props => getColor(props, 'primary')};
  color: ${props => getColor(props, 'onPrimary')};
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    border-color: ${props => getColor(props, 'accent')};
    background: ${props => getColor(props, 'lightGrey')};
  }
`;

export const RotateAngleField = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  height: 2rem;
  padding: 0 0.5rem;
  border: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};
  border-radius: 1rem;
  background: ${props => getColor(props, 'primary')};
  color: ${props => getColor(props, 'onPrimary')};

  &:focus-within {
    border-color: ${props => getColor(props, 'accent')};
  }
`;

export const RotateAngleInput = styled.input`
  width: 3rem;
  border: none;
  outline: none;
  background: transparent;
  color: inherit;
  font-size: 0.8125rem;
  text-align: right;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

export const RotateBarDivider = styled.div`
  width: 0.0625rem;
  height: 1.5rem;
  background: ${props => getColor(props, 'mediumGrey')};
  margin: 0 0.125rem;
`;

export const AdjustPanel = styled.div`
  position: absolute;
  top: 1.25rem;
  right: 1.25rem;
  width: 19rem;
  max-width: calc(100% - 2.5rem);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background: ${props => getColor(props, 'primary')};
  border: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};
  border-radius: 0.75rem;
  padding: 0.875rem 1rem;
  box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.18);
  z-index: 5;

  @media (max-width: 768px) {
    top: auto;
    right: 0.75rem;
    left: 0.75rem;
    bottom: 0.75rem;
    width: auto;
  }
`;

export const AdjustRow = styled.div`
  display: grid;
  grid-template-columns: 5.5rem 1fr 2rem;
  align-items: center;
  gap: 0.625rem;
`;

export const AdjustLabel = styled.label`
  font-size: 0.8125rem;
  color: ${props => getColor(props, 'onPrimary')};
  user-select: none;
`;

export const AdjustSlider = styled.input.attrs({ type: 'range' })`
  width: 100%;
  height: 0.25rem;
  -webkit-appearance: none;
  appearance: none;
  background: ${props => getColor(props, 'mediumGrey')};
  border-radius: 0.125rem;
  cursor: pointer;
  outline: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 0.875rem;
    height: 0.875rem;
    background: ${props => getColor(props, 'accent')};
    border-radius: 50%;
    cursor: pointer;
    border: none;
  }

  &::-moz-range-thumb {
    width: 0.875rem;
    height: 0.875rem;
    background: ${props => getColor(props, 'accent')};
    border-radius: 50%;
    cursor: pointer;
    border: none;
  }
`;

export const AdjustValue = styled.span`
  font-size: 0.75rem;
  color: ${props => mutedText(props)};
  text-align: right;
  font-variant-numeric: tabular-nums;
  user-select: none;
`;

export const AdjustActionRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.25rem;
`;

export const HiddenSvg = styled.svg`
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
`;

export const ResizePanel = styled.div`
  position: absolute;
  top: 1.25rem;
  right: 1.25rem;
  width: 17rem;
  max-width: calc(100% - 2.5rem);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background: ${props => getColor(props, 'primary')};
  border: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};
  border-radius: 0.75rem;
  padding: 0.875rem 1rem;
  box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.18);
  z-index: 5;

  @media (max-width: 768px) {
    top: auto;
    right: 0.75rem;
    left: 0.75rem;
    bottom: 0.75rem;
    width: auto;
  }
`;

export const ResizeRow = styled.div`
  display: grid;
  grid-template-columns: 4rem 1fr 1.25rem;
  align-items: center;
  gap: 0.5rem;
`;

export const ResizeLabel = styled.label`
  font-size: 0.8125rem;
  color: ${props => getColor(props, 'onPrimary')};
  user-select: none;
`;

export const ResizeInput = styled.input`
  width: 100%;
  height: 2rem;
  padding: 0 0.5rem;
  border: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};
  border-radius: 0.375rem;
  background: ${props => getColor(props, 'primary')};
  color: ${props => getColor(props, 'onPrimary')};
  font-size: 0.875rem;
  outline: none;
  font-variant-numeric: tabular-nums;

  &:focus {
    border-color: ${props => getColor(props, 'accent')};
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

export const ResizeUnit = styled.span`
  font-size: 0.75rem;
  color: ${props => mutedText(props)};
  user-select: none;
`;

export const ResizeLockRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const ResizeLockCheckbox = styled.input.attrs({ type: 'checkbox' })`
  width: 0.875rem;
  height: 0.875rem;
  accent-color: ${props => getColor(props, 'accent')};
  cursor: pointer;
`;

export const ResizeLockLabel = styled.label`
  font-size: 0.8125rem;
  color: ${props => getColor(props, 'onPrimary')};
  user-select: none;
  cursor: pointer;
`;

export const WorkflowBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

export const WorkflowStep = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.3125rem 0.75rem;
  border-radius: 1.25rem;
  border: 0.0625rem solid ${props => props.$active
    ? getColor(props, 'accent')
    : 'transparent'};
  background: ${props => props.$active
    ? props.theme.hexToRgba(getColor(props, 'accent'), 0.12)
    : 'transparent'};
  transition: background 0.15s ease, border-color 0.15s ease;
`;

export const WorkflowStepNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.375rem;
  height: 1.375rem;
  border-radius: 50%;
  font-size: 0.75rem;
  font-weight: 700;
  background: ${props => {
    if (props.$active) return getColor(props, 'accent');
    if (props.$completed) return getColor(props, 'success');
    return getColor(props, 'mediumGrey');
  }};
  color: ${props => (props.$active || props.$completed) ? '#ffffff' : mutedText(props)};
  flex-shrink: 0;
`;

export const WorkflowStepLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: ${props => props.$active ? 600 : 500};
  color: ${props => {
    if (props.$active) return getColor(props, 'accent');
    if (props.$completed) return getColor(props, 'onPrimary');
    return mutedText(props);
  }};
  white-space: nowrap;
`;

export const WorkflowDivider = styled.span`
  width: 1.5rem;
  height: 0.0625rem;
  background: ${props => getColor(props, 'mediumGrey')};
  flex-shrink: 0;

  @media (max-width: 480px) {
    display: none;
  }
`;

export const StatusBanner = styled.div`
  position: absolute;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 1.25rem;
  font-size: 0.8125rem;
  font-weight: 500;
  background: ${props => {
    if (props.$variant === 'error') return getColor(props, 'error');
    if (props.$variant === 'success') return getColor(props, 'success');
    return getColor(props, 'accent');
  }};
  color: #ffffff;
  box-shadow: 0 0.25rem 0.75rem rgba(0, 0, 0, 0.18);
  z-index: 6;
  max-width: calc(100% - 2rem);
`;

export const StatusSpinner = styled.span`
  display: inline-block;
  width: 0.875rem;
  height: 0.875rem;
  border: 0.125rem solid rgba(255, 255, 255, 0.35);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: statusSpin 0.8s linear infinite;

  @keyframes statusSpin {
    to { transform: rotate(360deg); }
  }
`;

export const SidebarHelperText = styled.p`
  font-size: 0.75rem;
  line-height: 1.4;
  color: ${props => mutedText(props)};
  background: ${props => subtleSurface(props)};
  border-radius: 0.5rem;
  padding: 0.5rem 0.625rem;
  margin: 0 0 0.75rem;
`;

export const ImagesSidebarPanel = styled.aside`
  width: 15rem;
  flex-shrink: 0;
  background: ${props => getColor(props, 'primary')};
  border-right: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  @media (max-width: 1024px) {
    display: none;
  }
`;

export const ImagesSidebarContent = styled.div`
  padding: 1.25rem 1rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const ImagesSidebarHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.125rem;
`;

export const ImagesSidebarTitle = styled.h2`
  font-size: 0.9375rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: ${props => getColor(props, 'onPrimary')};
  margin: 0;
`;

export const ImagesSidebarDescription = styled.p`
  font-size: 0.75rem;
  line-height: 1.45;
  color: ${props => mutedText(props)};
  margin: 0;
`;

export const SlotCardWrapper = styled.div`
  position: relative;
`;

export const SlotCard = styled.button`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  padding: 0.625rem 2.125rem 0.625rem 0.6875rem;
  border-radius: 0.625rem;
  border: 0.0625rem solid
    ${props => (props.$selected ? getColor(props, 'accent') : getColor(props, 'mediumGrey'))};
  background: ${props => (props.$selected
    ? props.theme.hexToRgba(getColor(props, 'accent'), 0.06)
    : getColor(props, 'primary'))};
  box-shadow: ${props => (props.$selected
    ? `0 0 0 0.1875rem ${props.theme.hexToRgba(getColor(props, 'accent'), 0.18)}`
    : 'none')};
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: ${props => getColor(props, 'accent')};
  }
`;

export const SlotBadge = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.375rem;
  height: 1.375rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: ${props => (props.$selected ? '#ffffff' : mutedText(props))};
  background: ${props => (props.$selected ? getColor(props, 'accent') : subtleSurface(props))};
`;

export const SlotBody = styled.span`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
  flex: 1;
`;

export const SlotTitle = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${props => getColor(props, 'onPrimary')};
  line-height: 1.3;
`;

export const SlotSubtitle = styled.span`
  font-size: 0.6875rem;
  line-height: 1.35;
  color: ${props => mutedText(props)};
`;

export const SlotThumb = styled.img`
  flex-shrink: 0;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.5rem;
  object-fit: cover;
  background: ${props => subtleSurface(props)};
`;

export const SlotThumbEmpty = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.5rem;
  border: 0.0625rem dashed ${props => getColor(props, 'mediumGrey')};
  color: ${props => mutedText(props)};
  font-size: 1.125rem;
  line-height: 1;
`;

export const SlotMenuButton = styled.span`
  position: absolute;
  top: 0.4375rem;
  right: 0.4375rem;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.375rem;
  height: 1.375rem;
  border-radius: 0.375rem;
  background: ${props => subtleSurface(props)};
  color: ${props => getColor(props, 'onPrimary')};
  font-size: 1.0625rem;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${props => props.theme.hexToRgba(getColor(props, 'accent'), 0.16)};
    color: ${props => getColor(props, 'accent')};
  }
`;

export const SlotMenu = styled.div`
  position: absolute;
  top: 1.875rem;
  right: 0.375rem;
  z-index: 5;
  min-width: 8rem;
  padding: 0.25rem;
  border-radius: 0.5rem;
  border: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};
  background: ${props => getColor(props, 'primary')};
  box-shadow: 0 0.5rem 1.25rem rgba(0, 0, 0, 0.14),
    0 0.0625rem 0.125rem rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
`;

export const SlotMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.4375rem 0.5rem;
  border: none;
  border-radius: 0.375rem;
  background: transparent;
  color: ${props => (props.$danger ? '#e5484d' : getColor(props, 'onPrimary'))};
  font-size: 0.8125rem;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${props => subtleSurface(props)};
  }
`;

export const SlotMenuBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 4;
`;

export const AddMoreButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.125rem;
  width: 100%;
  padding: 0.75rem;
  border-radius: 0.625rem;
  border: 0.0625rem dashed ${props => getColor(props, 'mediumGrey')};
  background: transparent;
  color: ${props => getColor(props, 'onPrimary')};
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${props => getColor(props, 'accent')};
    color: ${props => getColor(props, 'accent')};
    background: ${props => props.theme.hexToRgba(getColor(props, 'accent'), 0.05)};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const AddMoreLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
`;

export const AddMoreHelper = styled.span`
  font-size: 0.6875rem;
  color: ${props => mutedText(props)};
`;

export const SidebarTipsCard = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  margin-top: 0.25rem;
  padding: 0.75rem;
  border-radius: 0.625rem;
  background: ${props => subtleSurface(props)};
`;

export const SidebarTipsIcon = styled.span`
  font-size: 0.9375rem;
  line-height: 1.2;
  flex-shrink: 0;
`;

export const SidebarTipsBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`;

export const SidebarTipsTitle = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${props => mutedText(props)};
`;

export const SidebarTipsText = styled.span`
  font-size: 0.6875rem;
  line-height: 1.45;
  color: ${props => mutedText(props)};
`;

export const ImageActionsRow = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  display: flex;
  gap: 0.375rem;
  z-index: 4;
`;

export const HistoryControlBar = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 0.125rem;
  padding: 0.25rem;
  background: ${props => getColor(props, 'primary')};
  border: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};
  border-radius: 1.5rem;
  box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.12),
    0 0.0625rem 0.125rem rgba(0, 0, 0, 0.06);
  z-index: 4;
  backdrop-filter: blur(0.5rem);
`;

export const HistoryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  min-width: 2.25rem;
  height: 2.25rem;
  padding: 0 ${props => props.$wide ? '0.75rem' : '0'};
  border: none;
  border-radius: 1.125rem;
  background: transparent;
  color: ${props => getColor(props, 'onPrimary')};
  cursor: pointer;
  font-size: ${props => props.$wide || props.$primary ? '0.8125rem' : '1.0625rem'};
  font-weight: ${props => props.$primary ? 600 : 500};
  line-height: 1;
  transition: background 0.15s ease, opacity 0.15s ease, transform 0.1s ease, color 0.15s ease;

  ${props => props.$primary && `
    padding: 0 0.75rem;
    color: ${getColor(props, 'accent')};
    background: ${props.theme.hexToRgba(getColor(props, 'accent'), 0.12)};
    box-shadow: inset 0 0 0 0.0625rem ${props.theme.hexToRgba(getColor(props, 'accent'), 0.4)};
  `}

  &:hover:not(:disabled) {
    background: ${props => props.$primary
    ? props.theme.hexToRgba(getColor(props, 'accent'), 0.2)
    : getColor(props, 'lightGrey')};
    color: ${props => getColor(props, 'accent')};
  }

  &:active:not(:disabled) {
    transform: scale(0.94);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.35;
  }
`;

export const HistoryDivider = styled.span`
  width: 0.0625rem;
  height: 1.25rem;
  background: ${props => getColor(props, 'mediumGrey')};
  margin: 0 0.25rem;
`;

export const ImageActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 1rem;
  border: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};
  background: ${props => getColor(props, 'primary')};
  color: ${props => getColor(props, 'onPrimary')};
  cursor: pointer;
  box-shadow: 0 0.125rem 0.375rem rgba(0, 0, 0, 0.08);
  transition: background 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${props => getColor(props, 'accent')};
    background: ${props => getColor(props, 'lightGrey')};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const SidebarSection = styled.div`
  margin-bottom: 1.25rem;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

export const ThumbStripBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-top: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};
  background: ${props => getColor(props, 'primary')};
  flex-shrink: 0;
`;

export const ThumbStripCount = styled.span`
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${props => mutedText(props)};
  margin-right: 0.25rem;
`;

export const ThumbTile = styled.button`
  position: relative;
  width: 3.75rem;
  height: 3.75rem;
  padding: 0;
  border-radius: 0.5rem;
  border: 0.125rem solid
    ${props => (props.$active ? getColor(props, 'accent') : getColor(props, 'mediumGrey'))};
  background: ${props => getColor(props, 'lightGrey')};
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.15s ease, transform 0.1s ease, box-shadow 0.15s ease;
  box-shadow: ${props => (props.$active
    ? `0 0 0 0.125rem ${props.theme.hexToRgba(getColor(props, 'accent'), 0.2)}`
    : 'none')};

  &:hover {
    border-color: ${props => getColor(props, 'accent')};
  }

  &:active {
    transform: scale(0.96);
  }
`;

export const ThumbTileImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const ThumbTileRemove = styled.span`
  position: absolute;
  top: 0.1875rem;
  right: 0.1875rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #ffffff;
  font-size: 0.6875rem;
  line-height: 1;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s ease, background 0.15s ease;

  ${ThumbTile}:hover & {
    opacity: 1;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
`;

export const ThumbAddTile = styled.button`
  width: 3.75rem;
  height: 3.75rem;
  padding: 0;
  border-radius: 0.5rem;
  border: 0.125rem dashed ${props => getColor(props, 'mediumGrey')};
  background: transparent;
  color: ${props => mutedText(props)};
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: ${props => getColor(props, 'accent')};
    color: ${props => getColor(props, 'accent')};
    background: ${props => props.theme.hexToRgba(getColor(props, 'accent'), 0.06)};
  }
`;

export const GenerateButtonSpinner = styled.span`
  display: inline-block;
  width: 0.875rem;
  height: 0.875rem;
  border: 0.125rem solid rgba(255, 255, 255, 0.4);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: generateSpin 0.8s linear infinite;

  @keyframes generateSpin {
    to { transform: rotate(360deg); }
  }
`;

export const ModeToggleBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 0.375rem 1rem;
  border-bottom: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};
  background: ${props => getColor(props, 'primary')};
  flex-shrink: 0;
`;

export const ModeToggleButton = styled.button`
  padding: 0.3125rem 1.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
  border: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};
  background: ${props => props.$active
    ? getColor(props, 'accent')
    : getColor(props, 'primary')};
  color: ${props => props.$active ? '#ffffff' : getColor(props, 'onPrimary')};
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:first-child {
    border-radius: 0.375rem 0 0 0.375rem;
    border-right: none;
  }

  &:last-child {
    border-radius: 0 0.375rem 0.375rem 0;
  }

  &:hover:not(:disabled) {
    background: ${props => props.$active
      ? getColor(props, 'accentHover')
      : getColor(props, 'lightGrey')};
  }
`;

export const SimpleFieldLabel = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${props => mutedText(props)};
  margin: 0.625rem 0 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

export const SimpleToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${props => mutedText(props)};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

export const SimpleToggleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const SimpleFieldInput = styled.input`
  display: block;
  width: 100%;
  height: 2.25rem;
  padding: 0 0.625rem;
  border: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};
  border-radius: 0.375rem;
  background: ${props => getColor(props, 'primary')};
  color: ${props => getColor(props, 'onPrimary')};
  font-size: 0.875rem;
  outline: none;
  margin-bottom: 0.125rem;
  box-sizing: border-box;

  &:focus {
    border-color: ${props => getColor(props, 'accent')};
  }

  &::placeholder {
    color: ${props => mutedText(props)};
    opacity: 0.6;
  }
`;

export const SimpleTagsBox = styled.div`
  display: flex;
  width: 100%;
  box-sizing: border-box;
  padding: 0.25rem;
  border: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};
  border-radius: 0.375rem;
  background: ${props => getColor(props, 'primary')};
  margin-bottom: 0.125rem;
`;

export const ModalMessageTitle = styled.div`
  font-weight: 700;
  margin-bottom: 0.5rem;
`;
